import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useGetDayActivityQuery } from '../../store/api/usersApi'
import { timeAgoShort, contentPreview } from '../../lib/avatar'
import { AI_REPLY_PREVIEW_TRUNCATE } from '../../lib/config'

interface TooltipState {
  visible: boolean
  text: string
  x: number
  y: number
}

interface Props {
  userId: string
  activity: Record<string, number>
  totalContributions: number
  isLoading?: boolean
  selectedYear: number | null
  onYearChange: (year: number | null) => void
}

const LEVELS = [
  { min: 0,  max: 0,  bg: 'var(--bg-elevated)',         label: 'No activity' },
  { min: 1,  max: 2,  bg: 'rgba(124,58,237,0.25)',      label: '1–2' },
  { min: 3,  max: 5,  bg: 'rgba(124,58,237,0.5)',       label: '3–5' },
  { min: 6,  max: 9,  bg: 'rgba(124,58,237,0.75)',      label: '6–9' },
  { min: 10, max: Infinity, bg: 'rgba(124,58,237,1.0)', label: '10+' },
]

function getLevel(count: number) {
  return LEVELS.find((l) => count >= l.min && count <= l.max) ?? LEVELS[0]!
}

// YYYY-MM-DD in UTC — matches what the backend stores and returns
function toLocalKey(d: Date): string {
  return d.toISOString().slice(0, 10)
}

// Build a grid of weeks (each week = Sun–Sat array of Dates)
// All dates are created at UTC midnight to match backend UTC date keys
// null  → rolling 52 weeks ending today
// year  → Jan 1 – Dec 31 of that year
function buildGrid(selectedYear: number | null, today: Date): { weeks: Date[][]; label: string } {
  // today is already UTC midnight (set in the component)
  let start: Date
  let end: Date
  let label: string

  if (selectedYear === null) {
    end = new Date(today)
    const endDay = end.getUTCDay()
    end.setUTCDate(end.getUTCDate() + (6 - endDay))    // to Saturday UTC
    start = new Date(end)
    start.setUTCDate(start.getUTCDate() - 52 * 7 + 1)  // back 52 weeks
    label = 'the last year'
  } else {
    // Jan 1 UTC
    start = new Date(Date.UTC(selectedYear, 0, 1))
    const startDay = start.getUTCDay()
    start.setUTCDate(start.getUTCDate() - startDay)     // back to Sunday UTC
    // Dec 31 UTC
    end = new Date(Date.UTC(selectedYear, 11, 31))
    const endDay = end.getUTCDay()
    end.setUTCDate(end.getUTCDate() + (6 - endDay))     // to Saturday UTC
    label = String(selectedYear)
  }

  const days: Date[] = []
  const cur = new Date(start)
  while (cur <= end) {
    days.push(new Date(cur))
    cur.setUTCDate(cur.getUTCDate() + 1)
  }

  const weeks: Date[][] = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }
  return { weeks, label }
}

const DAY_LABELS  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const CELL = 12
const GAP  = 2
const STEP = CELL + GAP   // 14px per column
const DAY_LABEL_W = 26    // px reserved for Mon/Wed/Fri labels

export default function ContributionGraph({ userId, activity, totalContributions, isLoading, selectedYear, onYearChange }: Props) {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i)

  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)

  const { weeks, label } = buildGrid(selectedYear, today)
  const yearTotal = Object.values(activity).reduce((s, v) => s + v, 0)

  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, text: '', x: 0, y: 0 })
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const showTooltip = (e: React.MouseEvent<HTMLDivElement>, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      visible: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    })
  }

  const hideTooltip = () => setTooltip(t => ({ ...t, visible: false }))

  const isOutOfRange = (day: Date): boolean => {
    if (selectedYear === null) return day > today
    return day.getUTCFullYear() !== selectedYear
  }

  // Month labels: place a label above the first column whose FIRST IN-RANGE day
  // belongs to a new month. This avoids Dec/Jan overlap from padding days.
  const monthLabels: { col: number; name: string }[] = []
  let lastLabelMonth = -1
  weeks.forEach((week, col) => {
    // Find first in-range day in this week
    const firstInRange = week.find(d => !isOutOfRange(d))
    if (!firstInRange) return
    const mo = firstInRange.getUTCMonth()
    if (mo !== lastLabelMonth) {
      monthLabels.push({ col, name: MONTH_NAMES[mo]! })
      lastLabelMonth = mo
    }
  })

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.2rem 0.55rem',
    borderRadius: '6px',
    border: active ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
    background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
    color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
    fontSize: '0.72rem',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
  })

  if (isLoading) {
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ height: '12px', width: '200px', background: 'var(--bg-elevated)', borderRadius: '4px', marginBottom: '1rem' }} />
        <div style={{ display: 'flex', gap: `${GAP}px` }}>
          {Array.from({ length: 53 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: `${GAP}px` }}>
              {Array.from({ length: 7 }).map((_, j) => (
                <div key={j} style={{ width: `${CELL}px`, height: `${CELL}px`, borderRadius: '2px', background: 'var(--bg-elevated)' }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem', gap: '1rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          {yearTotal} contribution{yearTotal !== 1 ? 's' : ''} in {label}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {/* Year picker */}
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            <button
              style={btnStyle(selectedYear === null)}
              onClick={() => onYearChange(null)}
              onMouseEnter={(e) => { if (selectedYear !== null) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)' }}
              onMouseLeave={(e) => { if (selectedYear !== null) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
            >
              Last year
            </button>
            {years.map((y) => (
              <button
                key={y}
                style={btnStyle(selectedYear === y)}
                onClick={() => onYearChange(y)}
                onMouseEnter={(e) => { if (selectedYear !== y) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)' }}
                onMouseLeave={(e) => { if (selectedYear !== y) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
              >
                {y}
              </button>
            ))}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginRight: '2px' }}>Less</span>
            {LEVELS.map((l, i) => (
              <div key={i} title={l.label} style={{ width: `${CELL - 2}px`, height: `${CELL - 2}px`, borderRadius: '2px', background: l.bg, border: '1px solid rgba(255,255,255,0.06)' }} />
            ))}
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: '2px' }}>More</span>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: 'max-content' }}>

          {/* Month labels — positioned by column index, based only on in-range days */}
          <div style={{ display: 'flex', marginLeft: `${DAY_LABEL_W}px`, marginBottom: '4px', position: 'relative', height: '14px' }}>
            {monthLabels.map(({ col, name }) => (
              <span
                key={`${col}-${name}`}
                style={{
                  position: 'absolute',
                  left: `${col * STEP}px`,
                  fontSize: '0.63rem',
                  color: 'var(--text-muted)',
                  lineHeight: '14px',
                  userSelect: 'none',
                }}
              >
                {name}
              </span>
            ))}
          </div>

          <div style={{ display: 'flex' }}>
            {/* Day-of-week labels: show Mon, Wed, Fri */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: `${GAP}px`, marginRight: '4px', width: `${DAY_LABEL_W - 4}px` }}>
              {DAY_LABELS.map((d, i) => (
                <div
                  key={d}
                  style={{
                    height: `${CELL}px`,
                    lineHeight: `${CELL}px`,
                    fontSize: '0.55rem',
                    color: [1, 3, 5].includes(i) ? 'var(--text-muted)' : 'transparent',
                    textAlign: 'right',
                    userSelect: 'none',
                  }}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Week columns */}
            <div style={{ display: 'flex', gap: `${GAP}px` }}>
              {weeks.map((week, wi) => (
                <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: `${GAP}px` }}>
                  {week.map((day, di) => {
                    const key = toLocalKey(day)
                    const count = activity[key] ?? 0
                    const level = getLevel(count)
                    const outOfRange = isOutOfRange(day)
                    // Format key (YYYY-MM-DD) as "April 20th"
                    const [, mo, dy] = key.split('-').map(Number)
                    const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][(mo ?? 1) - 1]
                    const suffix = dy === 1 || dy === 21 || dy === 31 ? 'st' : dy === 2 || dy === 22 ? 'nd' : dy === 3 || dy === 23 ? 'rd' : 'th'
                    const dateLabel = `${monthName} ${dy}${suffix}`
                    const tipText = outOfRange ? '' : count > 0
                      ? `${count} contribution${count > 1 ? 's' : ''} on ${dateLabel}`
                      : `No contributions on ${dateLabel}`

                    return (
                      <div
                        key={di}
                        style={{
                          width: `${CELL}px`,
                          height: `${CELL}px`,
                          borderRadius: '2px',
                          background: outOfRange ? 'transparent' : level.bg,
                          border: outOfRange ? 'none' : selectedDate === key ? '2px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.06)',
                          flexShrink: 0,
                          cursor: outOfRange ? 'default' : 'pointer',
                          transition: 'transform 0.1s',
                        }}
                        onMouseEnter={(e) => {
                          if (!outOfRange) {
                            (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.4)'
                            showTooltip(e, tipText)
                          }
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)'
                          hideTooltip()
                        }}
                        onClick={() => {
                          if (outOfRange) return
                          setSelectedDate(prev => prev === key ? null : key)
                        }}
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Portal tooltip */}
      {tooltip.visible && createPortal(
        <div style={{
          position: 'fixed',
          top: tooltip.y,
          left: tooltip.x,
          transform: 'translate(-50%, -100%)',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
          fontSize: '0.72rem',
          fontWeight: 500,
          padding: '0.3rem 0.65rem',
          borderRadius: '6px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 99999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          animation: 'cgTipFade 0.1s ease',
        }}>
          {tooltip.text}
          <style>{`@keyframes cgTipFade { from { opacity:0; transform:translate(-50%,calc(-100% + 4px)) } to { opacity:1; transform:translate(-50%,-100%) } }`}</style>
        </div>,
        document.body
      )}

      {/* Day detail panel */}
      {selectedDate && (
        <DayDetail
          userId={userId}
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  )
}

// ── Day detail panel ──────────────────────────────────────────
function DayDetail({ userId, date, onClose }: { userId: string; date: string; onClose: () => void }) {
  const { data, isLoading } = useGetDayActivityQuery({ userId, date })

  // Format date as "April 20th, 2026"
  const [yr, mo, dy] = date.split('-').map(Number)
  const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][(mo ?? 1) - 1]
  const suffix = dy === 1 || dy === 21 || dy === 31 ? 'st' : dy === 2 || dy === 22 ? 'nd' : dy === 3 || dy === 23 ? 'rd' : 'th'
  const dateLabel = `${monthName} ${dy}${suffix}, ${yr}`

  const total = (data?.discussions.length ?? 0) + (data?.comments.length ?? 0)

  return (
    <div style={{
      marginTop: '1rem',
      borderTop: '1px solid var(--border)',
      paddingTop: '1rem',
      animation: 'cgSlideDown 0.15s ease',
    }}>
      <style>{`@keyframes cgSlideDown { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          {isLoading ? 'Loading...' : `${total} contribution${total !== 1 ? 's' : ''} on ${dateLabel}`}
        </span>
        <button
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0.1rem 0.3rem' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
        >
          ✕
        </button>
      </div>

      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ height: '48px', background: 'var(--bg-elevated)', borderRadius: '8px' }} />
          ))}
        </div>
      )}

      {!isLoading && total === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          No activity recorded on this day
        </div>
      )}

      {!isLoading && data && total > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '280px', overflowY: 'auto', paddingRight: '0.25rem' }}>
          {/* Discussions */}
          {data.discussions.map((d) => (
            <Link
              key={d.id}
              to={`/discussions/${d.id}`}
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.65rem 0.875rem',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                transition: 'border-color 0.15s, background 0.15s',
              }}
                onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--accent-primary)'; el.style.background = 'rgba(124,58,237,0.05)' }}
                onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>
                  {d.categories[0]?.icon ?? '💬'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.title}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem', display: 'flex', gap: '0.5rem' }}>
                    {d.categories[0] && <span style={{ color: d.categories[0].color ?? 'var(--accent-primary)' }}>{d.categories[0].name}</span>}
                    <span>💬 {d.commentCount}</span>
                    <span>↑ {d.voteScore}</span>
                    <span>{timeAgoShort(d.createdAt)}</span>
                  </div>
                </div>
                <span style={{ fontSize: '0.68rem', background: 'rgba(124,58,237,0.12)', color: 'var(--accent-primary)', padding: '0.15rem 0.45rem', borderRadius: '6px', flexShrink: 0, fontWeight: 600 }}>Discussion</span>
              </div>
            </Link>
          ))}

          {/* Comments */}
          {data.comments.map((c) => (
            <Link
              key={c.id}
              to={`/discussions/${c.discussionId}`}
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                padding: '0.65rem 0.875rem',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                transition: 'border-color 0.15s, background 0.15s',
              }}
                onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--accent-primary)'; el.style.background = 'rgba(124,58,237,0.05)' }}
                onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '0.1rem' }}>↩</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {contentPreview(c.content, AI_REPLY_PREVIEW_TRUNCATE)}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                    in <span style={{ color: 'var(--accent-primary)' }}>{c.discussion?.title ?? 'a discussion'}</span>
                    <span style={{ margin: '0 0.3rem' }}>·</span>
                    {timeAgoShort(c.createdAt)}
                  </div>
                </div>
                <span style={{ fontSize: '0.68rem', background: 'var(--bg-card)', color: 'var(--text-muted)', padding: '0.15rem 0.45rem', borderRadius: '6px', flexShrink: 0, border: '1px solid var(--border)' }}>Reply</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
